import { useEffect, useRef, useState, type ReactNode } from 'react'

/**
 * Asks before an action whose blast radius is bigger than the control suggests
 * — one small switch taking every translated page out of service.
 *
 * Cancel sits on the left and is focused on open, so the safe answer is both
 * the default and the one a stray Enter or Escape gives you.
 */
export function ConfirmDialog({
	title,
	body,
	cancelLabel,
	confirmLabel,
	onCancel,
	onConfirm,
	confirmWord,
	children,
}: {
	title: string
	body: string
	cancelLabel: string
	confirmLabel: string
	onCancel: () => void
	onConfirm: () => void
	/**
	 * When set, the confirm button stays disabled until the user types this
	 * exact text. Reserve it for the genuinely unrecoverable actions — asking
	 * someone to copy a domain name to pause translations would just teach them
	 * to type without reading.
	 */
	confirmWord?: string
	/** Extra detail above the field — e.g. what becomes safe to delete. */
	children?: ReactNode
}) {
	const cancelRef = useRef<HTMLButtonElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)
	const [typed, setTyped] = useState('')

	const ready = !confirmWord || typed.trim() === confirmWord

	useEffect(() => {
		// With a field to fill in, put the cursor there; otherwise focus Cancel so
		// the safe answer is what a stray Enter gives you.
		if (confirmWord) {
			inputRef.current?.focus()
		} else {
			cancelRef.current?.focus()
		}
		function onKey(e: KeyboardEvent) {
			if (e.key === 'Escape') {
				onCancel()
			}
		}
		document.addEventListener('keydown', onKey)
		return () => document.removeEventListener('keydown', onKey)
	}, [onCancel, confirmWord])

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4"
			onMouseDown={(e) => {
				if (e.target === e.currentTarget) {
					onCancel()
				}
			}}
		>
			<div
				role="dialog"
				aria-modal="true"
				aria-label={title}
				className="max-h-[90vh] w-full max-w-[440px] overflow-y-auto rounded-nayda-lg border border-line bg-paper p-5 shadow-lg"
			>
				<h2 className="font-sans text-[17px] font-medium text-ink">{title}</h2>
				<p className="mt-2 text-[13px] leading-relaxed text-ink-soft">{body}</p>

				{children}

				{confirmWord && (
					<label className="mt-4 flex flex-col gap-1.5">
						<span className="text-[13px] text-ink">
							Type <span className="font-mono text-ink">{confirmWord}</span> to confirm
						</span>
						<input
							ref={inputRef}
							value={typed}
							onChange={(e) => setTyped(e.target.value)}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && ready) {
									onConfirm()
								}
							}}
							autoCapitalize="off"
							autoComplete="off"
							spellCheck={false}
							placeholder={confirmWord}
							className="field font-mono"
						/>
					</label>
				)}

				<div className="mt-5 flex items-center justify-between gap-3">
					<button
						ref={cancelRef}
						onClick={onCancel}
						className="btn btn-ghost h-10 px-4 text-[13px]"
					>
						{cancelLabel}
					</button>
					<button
						onClick={onConfirm}
						disabled={!ready}
						className="btn btn-danger h-10 px-4 text-[13px]"
					>
						{confirmLabel}
					</button>
				</div>
			</div>
		</div>
	)
}
